import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CheckboxModule } from 'primeng/checkbox';
import { LambdaService } from 'src/app/services/lambda.service';
import { KnowledgebaseService, KnowledgeBaseEntry } from 'src/app/services/knowledgebase.service';
import { SpinnerService } from 'src/app/services/spinner.service';

interface Shop {
  id: number;
  name: string;
}

type TrainingEntry = KnowledgeBaseEntry;

interface MockDocument {
  name: string;
  date: string;
  status: 'processed' | 'pending';
}

interface MockImage {
  name: string;
  description: string;
}

const LAMBDA_ENVS = {
  dev:  { url: 'https://lambdas-dev.mobydyg.com', key: 'dev123!' },
  prod: { url: 'https://lambdas.mobydyg.com',     key: 'RnNWU0U1FUypwP3LxqbMWMQ5QzYaVXx46XiiquSGG3qj4oa5p804NTiqLzdk69SMQNHtzYuh95IkTeWU3WMBTcCOFzo5rGyj8x1N' }
};

@Component({
  selector: 'app-ai-training-workspace',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ConfirmDialogModule,
    TooltipModule,
    TextareaModule,
    ProgressSpinnerModule,
    TabsModule,
    SelectButtonModule,
    CheckboxModule
  ],
  templateUrl: './ai-Training-Workspace.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService, ConfirmationService]
})
export class AiTrainingWorkspace implements OnInit {
  shops: Shop[] = [];
  trainingEntries: TrainingEntry[] = [];
  selectedShopId: number | null = null;

  envMode: 'dev' | 'prod' = 'dev';
  envOptions = [
    { label: 'Dev', value: 'dev' },
    { label: 'Prod', value: 'prod' }
  ];
  showDialog = false;
  isEditMode = false;
  loading = false;
  isSaving = false;
  isDeleting = false;
  showPreviewDialog = false;
  previewEntry: TrainingEntry | null = null;
  form!: FormGroup;

  // --- Header ---
  agentName = 'Mi Agente IA';
  agentStatus: 'active' | 'training' | 'unpublished' = 'training';

  get statusLabel(): string {
    const map = { active: 'Activo', training: 'En entrenamiento', unpublished: 'Sin publicar' };
    return map[this.agentStatus];
  }

  get statusBadgeClass(): string {
    const map = {
      active: 'bg-green-100 text-green-700 border border-green-200',
      training: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
      unpublished: 'bg-red-100 text-red-700 border border-red-200'
    };
    return map[this.agentStatus];
  }

  get statusDotClass(): string {
    const map = { active: 'bg-green-500', training: 'bg-yellow-500', unpublished: 'bg-red-500' };
    return map[this.agentStatus];
  }

  mockDocuments: MockDocument[] = [
    { name: 'Manual de usuario v2.pdf', date: '01/05/2026', status: 'processed' }
  ];

  mockImages: MockImage[] = [
    { name: 'Panel de control del vehículo.jpg', description: 'Esta imagen muestra el panel de control del vehículo con todos sus indicadores...' },
  ];

  selectedTone = 'formal';
  selectedDetailLevel = 'medium';
  noInventar = true;
  onlyDocuments = false;
  selectedLanguage = 'es';

  toneOptions = [
    { label: 'Formal', value: 'formal' },
    { label: 'Cercano', value: 'friendly' },
    { label: 'Técnico', value: 'technical' }
  ];

  detailOptions = [
    { label: 'Corto', value: 'short' },
    { label: 'Medio', value: 'medium' },
    { label: 'Explicativo', value: 'detailed' }
  ];

  languageOptions = [
    { label: 'Español', value: 'es' },
    { label: 'English', value: 'en' },
    { label: 'Português', value: 'pt' }
  ];

  constructor(
    private lambdaService: LambdaService,
    private knowledgebaseService: KnowledgebaseService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private spinnerService: SpinnerService
  ) {
    this.initForm();
  }

  ngOnInit() {
    this.loadShops();
  }

  initForm() {
    this.form = this.fb.group({
      id: [null],
      subject: ['', Validators.required],
      context: ['', Validators.required],
      category: ['']
    });
  }

  loadShops() {
    this.spinnerService.showSpinner();
    const env = LAMBDA_ENVS[this.envMode];
    fetch(`${env.url}/shops`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': env.key
      }
    })
      .then(r => r.json())
      .then((data: any) => {
        const shopsArray = Array.isArray(data) ? data : (data?.data || data?.shops || []);
        this.shops = shopsArray
          .filter((shop: any) => !!shop.virtual_agents)
          .map((shop: any) => ({
            id: shop.shopId,
            name: shop.shopName
          }));
        this.spinnerService.hideSpinner();
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las tiendas' });
        this.spinnerService.hideSpinner();
      });
  }

  onEnvChange() {
    this.selectedShopId = null;
    this.trainingEntries = [];
    this.knowledgebaseService.setEnv(this.envMode);
    this.loadShops();
    this.cdr.markForCheck();
  }

  onShopChange(event: any) {
    this.selectedShopId = event.value;
    if (this.selectedShopId) {
      this.loadTrainingEntries(this.selectedShopId);
    } else {
      this.trainingEntries = [];
      this.cdr.markForCheck();
    }
  }

  loadTrainingEntries(shopId: number) {
    this.loading = true;
    this.cdr.markForCheck();
    this.knowledgebaseService.getByShopId(shopId).then(
      (entries) => {
        this.trainingEntries = entries;
        this.loading = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las entradas' });
        this.loading = false;
        this.cdr.markForCheck();
      }
    );
  }

  openDialog(entry?: TrainingEntry) {
    if (entry) {
      this.isEditMode = true;
      this.form.patchValue({
        id: entry.id,
        subject: entry.subject,
        context: entry.context,
        category: entry.category
      });
    } else {
      this.isEditMode = false;
      this.form.reset();
    }
    this.showDialog = true;
  }

  closeDialog() {
    this.showDialog = false;
    this.form.reset();
  }

  saveEntry() {
    if (this.form.invalid || !this.selectedShopId) return;

    this.isSaving = true;
    const { id, subject, context, category } = this.form.value;
    const payload = { shopId: this.selectedShopId, subject, context, category };

    const request = this.isEditMode
      ? this.knowledgebaseService.update(id, payload)
      : this.knowledgebaseService.create(payload);

    request.then(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode ? 'Entrada actualizada' : 'Entrada creada'
        });
        this.closeDialog();
        this.loadTrainingEntries(this.selectedShopId!);
        this.isSaving = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la entrada' });
        this.isSaving = false;
        this.cdr.markForCheck();
      }
    );
  }

  openPreview(entry: TrainingEntry) {
    this.previewEntry = entry;
    this.showPreviewDialog = true;
  }

  deleteEntry(entry: TrainingEntry) {
    this.confirmationService.confirm({
      message: `¿Estás seguro de eliminar esta entrada?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.isDeleting = true;
        this.cdr.markForCheck();
        this.knowledgebaseService.delete(entry.id).then(
          () => {
            this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Entrada eliminada' });
            this.isDeleting = false;
            this.loadTrainingEntries(this.selectedShopId!);
          },
          (error: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la entrada' });
            this.isDeleting = false;
            this.cdr.markForCheck();
          }
        );
      }
    });
  }

}

